import { Map, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trackEmailFocus, trackEmailEntered, trackCTAClick } from '@/lib/analytics';

interface FooterProps {
  email: string;
  setEmail: (email: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isSubmitting?: boolean;
}

const Footer = ({ email, setEmail, handleSubmit, isSubmitting = false }: FooterProps) => {
  const formSource = 'homepage_footer';

  const handleEmailFocus = () => {
    trackEmailFocus(formSource);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    trackEmailEntered(formSource, newEmail);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    trackCTAClick(formSource, email);
    handleSubmit(e);
  };

  return (
    <>
      {/* Final CTA */}
      <section className="container mx-auto px-4 py-16 text-center relative z-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
            Interested? Get in touch.
          </h2>
          
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-blue-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <form onSubmit={handleFormSubmit} className="flex gap-4 items-center max-w-2xl mx-auto">
              <Input
                type="email"
                placeholder="trailblazer@yourbusiness.com"
                value={email}
                onChange={handleEmailChange}
                onFocus={handleEmailFocus}
                className="flex-1 border-blue-200 focus:border-blue-400 rounded-xl transition-all duration-300 focus:shadow-lg min-w-0"
                required
                disabled={isSubmitting}
              />
              <Button 
                type="submit" 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "I'm interested"
                )}
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-4">
              Built for busy business owners. No dashboards. No clutter. Just clarity.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8 relative z-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Map className="h-6 w-6 text-blue-400" />
              <span className="font-bold text-lg">Carto</span>
            </div>
            <div className="text-sm text-slate-400">
              © 2024 Carto. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
